<template>
  <div class="chart-pyramid" :id="`chart-pyramid-${chartId}`" />
</template>
<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import kebabCase from 'lodash-es/kebabCase';

am4core.addLicense('CH245905005');
am4core.addLicense('TL245443410');
am4core.useTheme(am4themesAnimated);
export default {
  name: 'PyramidViz',
  props: {
    category: {
      type: String,
      required: true,
    },
    frequency: {
      type: String,
      required: true,
    },
    country: {
      type: String,
      required: true,
    },
  },
  watch: {
    category(newVal) {
      const updatedData = this.filterData(newVal, this.frequency);
      this.updateChart(updatedData);
    },
    frequency(newVal) {
      const updatedData = this.filterData(this.category, newVal);
      this.updateChart(updatedData);
    },
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  computed: {
    chartId() {
      return kebabCase(this.country);
    },
    initData() {
      const init = this.sanitisedData.map(({ percFemale, percMaleNeg, ...keepProps }) => {
        return { ...keepProps, percFemale: Number(percFemale * 0), percMaleNeg: Number(percMaleNeg * 0) };
      });
      return init;
    },
  },
  data() {
    return {
      typeDefault: '',
      sanitisedData: null,
    };
  },
  mounted() {
    // load data
    const dataSource = new am4core.DataSource();
    dataSource.url = `${process.env.BASE_URL}data/A1_pyramid.csv`;
    dataSource.parser = new am4core.CSVParser();
    dataSource.parser.options.emptyAs = '';
    dataSource.parser.options.useColumnNames = true;
    dataSource.load();
    dataSource.events.on('done', ({ data }) => {
      this.sanitisedData = this.formulateData(data, this.category, this.country, this.frequency);
      this.chart = this.createChart();
      this.categoryAxis = this.createAxisY();
      this.valueAxis = this.createAxisX();
      this.createSeries();
      this.createLabel(this.country);
      this.updateChart(this.sanitisedData);
    });
  },
  methods: {
    createChart() {
      // create XY chart
      const chart = am4core.create(`chart-pyramid-${this.chartId}`, am4charts.XYChart);
      if (chart.logo) {
        chart.logo.disabled = true;
      }
      chart.numberFormatter.numberFormat = '#.#s';
      chart.padding(30, 18, 10, 10);
      if (this.$breakpoint.mobile) {
        chart.padding(30, 25, 10, 10);
      }
      chart.paddingLeft = 0;
      chart.zoomOutButton.disabled = true;
      chart.data = this.initData;
      return chart;
    },
    formulateData(rawData, paymentType, country, frequency) {
      const data = rawData.map(({ percMaleNeg, percFemale, ...props }) => {
        return { ...props, percMaleNeg: Number(percMaleNeg), percFemale: Number(percFemale) };
      });
      const sortingArray = ['Youths', 'Millennials', 'Gen Xers', 'Seniors'];
      data.sort((a, b) => {
        return sortingArray.indexOf(a.generation) - sortingArray.indexOf(b.generation);
      });
      this.dataByCountry = data.filter((d) => d.country === country);
      // filter by category and frequency
      const dataFiltered = this.dataByCountry
        .filter((d) => d.paymentType === paymentType)
        .filter((d) => d.frequency === frequency);
      return dataFiltered;
    },
    filterData(paymentType, frequency) {
      return this.dataByCountry.filter((d) => d.paymentType === paymentType).filter((d) => d.frequency === frequency);
    },
    createAxisY() {
      // y-axis - category axis
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'generation';
      // categoryAxis.sortBySeries = this.series;
      categoryAxis.renderer.inversed = true;
      // style settings
      categoryAxis.renderer.grid.template.disabled = false;
      categoryAxis.renderer.grid.template.location = 0.5;
      // cell width
      categoryAxis.renderer.cellStartLocation = 0.1;
      categoryAxis.renderer.cellEndLocation = 0.9;
      // labels
      categoryAxis.renderer.labels.template.location = 0.5;
      categoryAxis.renderer.minGridDistance = 0;
      categoryAxis.renderer.labels.template.dy = 0;
      categoryAxis.renderer.labels.template.dx = -5;
      categoryAxis.renderer.labels.template.fontSize = 14.5;
      categoryAxis.renderer.labels.template.fill = '#121212';
      if (this.$breakpoint.mobile) {
        categoryAxis.renderer.labels.template.fontSize = 11.5;
      }
      return categoryAxis;
    },
    createAxisX() {
      // x-axis - value axis
      const valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis());

      // Fix axis scale on load
      valueAxis.extraMin = 0.1;
      valueAxis.extraMax = 0.1;
      valueAxis.min = -100;
      valueAxis.max = 100;
      valueAxis.strictMinMax = true;

      // style settings
      valueAxis.renderer.labels.template.adapter.add('text', (label) => {
        return `${label}%`;
      });
      valueAxis.renderer.grid.template.stroke = '#F0F0F0';
      valueAxis.renderer.grid.template.strokeOpacity = 0;
      valueAxis.renderer.labels.template.fontSize = 12;
      valueAxis.renderer.labels.template.fill = '#595959';
      valueAxis.renderer.minGridDistance = 30;
      valueAxis.renderer.ticks.template.length = 1;
      valueAxis.renderer.ticks.template.strokeOpacity = 0;
      valueAxis.renderer.ticks.template.disabled = false;

      if (this.$breakpoint.mobile) {
        valueAxis.renderer.labels.template.fontSize = 8.5;
      }
      return valueAxis;
    },
    createSeries() {
      // Create series
      const maleSeries = this.chart.series.push(new am4charts.ColumnSeries());
      maleSeries.dataFields.valueX = 'percMaleNeg';
      maleSeries.dataFields.categoryY = 'generation';
      maleSeries.columns.template.fill = '#005EB8';
      // maleSeries.columns.template.stroke = '#005EB8';
      maleSeries.columns.template.strokeWidth = 0;
      maleSeries.clustered = false;
      maleSeries.columns.template.tooltipText = "[bold]{generation} (Males)[/]\n {percMaleNeg.formatNumber('#s')}%";
      maleSeries.tooltip.getFillFromObject = false;
      maleSeries.tooltip.background.fill = am4core.color('#ffffff');
      maleSeries.tooltip.label.fill = am4core.color("#121212");
      maleSeries.tooltip.background.cornerRadius = 4;
      maleSeries.tooltip.background.strokeOpacity = 0;
      maleSeries.tooltip.pointerOrientation = 'vertical';
      maleSeries.tooltip.animationDuration = 200;
      maleSeries.tooltip.defaultState.transitionDuration = 200;
      maleSeries.tooltip.hiddenState.transitionDuration = 200;
      maleSeries.tooltip.label.maxWidth = 200;
      maleSeries.tooltip.label.wrap = true;
      const hoverStateMale = maleSeries.columns.template.states.create('hover');
      hoverStateMale.properties.stroke = am4core.color('#121212');
      hoverStateMale.properties.strokeWidth = 2;
      const femaleSeries = this.chart.series.push(new am4charts.ColumnSeries());
      femaleSeries.dataFields.valueX = 'percFemale';
      femaleSeries.dataFields.categoryY = 'generation';
      femaleSeries.columns.template.fill = '#FDC4D0';
      // femaleSeries.columns.template.stroke = '#E1091D';
      femaleSeries.columns.template.strokeWidth = 0;

      femaleSeries.clustered = false;
      femaleSeries.columns.template.tooltipText = "[bold]{generation} (Females)[/]\n {percFemale.formatNumber('#s')}%";
      // "[bold]{generation} (Females)[/]\n {percFemale.formatNumber('#s')}% \n {paymentType}, {country}, {frequency} ";
      femaleSeries.tooltip.getFillFromObject = false;
      femaleSeries.tooltip.background.fill = am4core.color('#ffffff');
      femaleSeries.tooltip.label.fill = am4core.color("#121212");
      femaleSeries.tooltip.background.cornerRadius = 4;
      femaleSeries.tooltip.background.strokeOpacity = 0;
      femaleSeries.tooltip.pointerOrientation = 'vertical';
      femaleSeries.tooltip.animationDuration = 200;
      femaleSeries.tooltip.defaultState.transitionDuration = 200;
      femaleSeries.tooltip.hiddenState.transitionDuration = 200;
      femaleSeries.tooltip.label.maxWidth = 200;
      femaleSeries.tooltip.label.wrap = true;
      const hoverStateFemale = femaleSeries.columns.template.states.create('hover');
      hoverStateFemale.properties.stroke = am4core.color('#121212');
      hoverStateFemale.properties.strokeWidth = 2;
    },
    updateChart(data) {
      // get data by selected type
      const selectedData = data;
      // mutate existing value, so chart will animate the difference
      this.chart.data.forEach((d) => {
        d.percMaleNeg =
          selectedData.length > 0
            ? selectedData.find((i) => i.generation === d.generation && i.country === d.country).percMaleNeg
            : 0;
        d.percFemale =
          selectedData.length > 0
            ? selectedData.find((i) => i.generation === d.generation && i.country === d.country).percFemale
            : 0;
        d.paymentType =
          selectedData.length > 0
            ? selectedData.find((i) => i.generation === d.generation && i.country === d.country).paymentType
            : '';
        d.frequency =
          selectedData.length > 0
            ? selectedData.find((i) => i.generation === d.generation && i.country === d.country).frequency
            : '';
      });
      this.createSeries();
      this.chart.invalidateRawData();
    },
    createLabel(paymentType) {
      const label = this.chart.createChild(am4core.Label);
      label.isMeasured = false;
      label.x = am4core.percent(50);
      label.horizontalCenter = 'middle';
      label.y = -28;
      label.dx = 40;
      label.showOnInit = true;
      label.fontSize = 16;
      label.fontWeight = 'bold';
      label.text = paymentType;
    },
  },
};
</script>
<style scoped lang="scss">
.chart-pyramid {
  width: 100%;
  height: 215px;
  @media #{map-get($media-breakpoints, 'mobile')} {
    height: 195px;
  }
}
</style>
